"use client";
import { useState, useCallback, useRef, useEffect } from "react";
import { Copy, Check } from "lucide-react";
import { Button, CustomTooltip } from "@/app/components/ui";
import { ChatMessage } from "@/types";

type CopyContentProps = {
   msg: ChatMessage;
};
export const CopyContent = (props: CopyContentProps) => {
   const { msg } = props;
   const [isCopied, setIsCopied] = useState(false);
   const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   useEffect(() => {
      return () => {
         if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
   }, []);

   const handleCopy = useCallback(async () => {
      try {
         await navigator.clipboard.writeText(msg.content);
      } catch {
         // Fallback for insecure context or permission denied
         const textarea = document.createElement("textarea");
         textarea.value = msg.content;
         textarea.style.position = "fixed";
         textarea.style.opacity = "0";
         document.body.appendChild(textarea);
         textarea.select();
         document.execCommand("copy");
         document.body.removeChild(textarea);
      }
      setIsCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
   }, [msg.content]);

   return (
      <CustomTooltip text={isCopied ? "Copied!" : "Copy"} delayDuration={100}>
         <Button
            variant="ghost"
            size="xs"
            onClick={handleCopy}
            className="flex items-center text-xs text-gray-500 hover:text-blue-500 whitespace-nowrap"
         >
            {isCopied ? (
               <Check className="w-4 h-4 mr-1 text-primary/80" />
            ) : (
               <Copy className="w-4 h-4 mr-1" />
            )}
         </Button>
      </CustomTooltip>
   );
};
